import { Redirect, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import BigNumber from 'bignumber.js';
import queryString from 'query-string';

import { useStaticPoolDataContext } from '../../../../libs/pool-data-provider';
import { useStakeDataContext } from '../../../../libs/pool-data-provider/hooks/use-stake-data-context';
import Row from '../../../../components/basic/Row';
import Value from '../../../../components/basic/Value';

import messages from './messages';
import StakeTxConfirmationView from '../../components/StakeTxConfirmationView';
import { getProvider } from '../../../../helpers/config/markets-and-network-config';
import { PhiatFeeDistribution__factory } from '../../../../libs/pool-data-provider/contracts/factories/PhiatFeeDistribution__factory';
import {
  eEthereumTxType,
  EthereumTransactionTypeExtended,
  transactionType,
} from '@aave/contract-helpers';

export default function UnstakeConfirmation() {
  const intl = useIntl();
  const location = useLocation();
  const { userId } = useStaticPoolDataContext();
  const { chainId, stakeConfig, stakeData, stakingService } = useStakeDataContext();

  const query = queryString.parse(location.search);
  let amount = new BigNumber(typeof query.amount === 'string' ? query.amount : 0);

  if (amount.eq(0) || !userId) {
    return null;
  }
  if (!stakeData.userCanUnstake) {
    return <Redirect to="/staking" />;
  }

  let blockingError = '';
  const userStakedBalance = new BigNumber(stakeData.userStakedBalance);
  if (amount.gt(userStakedBalance)) {
    blockingError = intl.formatMessage(messages.blockingError);
  }
  const convertedAmount = amount
    .multipliedBy(new BigNumber(10).exponentiatedBy(stakeData.stakingTokenPrecision))
    .toFixed(0);

  const handleGetTransactions = async (): Promise<EthereumTransactionTypeExtended[]> => {
    const stakingContract = PhiatFeeDistribution__factory.connect(
      stakeConfig.feeDistribution,
      getProvider(chainId)
    );
    const txCallback: () => Promise<transactionType> = stakingService.generateTxCallback({
      rawTxMethod: async () => stakingContract.populateTransaction.unstake(convertedAmount),
      from: userId,
      gasSurplus: 20,
    });
    return [
      {
        tx: txCallback,
        txType: eEthereumTxType.STAKE_ACTION,
        gas: stakingService.generateTxPriceEstimation([], txCallback),
      },
    ];
  };

  return (
    <StakeTxConfirmationView
      caption={intl.formatMessage(messages.title)}
      description={intl.formatMessage(messages.description)}
      getTransactionsData={handleGetTransactions}
      boxTitle={intl.formatMessage(messages.unstakeAsset, {
        asset: stakeConfig.stakingTokenSymbol,
      })}
      boxDescription={intl.formatMessage(messages.boxDescription)}
      mainTxName={intl.formatMessage(messages.unstake)}
      goToAfterSuccess="/staking"
      successButtonTitle={intl.formatMessage(messages.backToStaking)}
      blockingError={blockingError}
      buttonTitle={intl.formatMessage(messages.unstake)}
    >
      <Row
        title={intl.formatMessage(messages.unstakeAsset, {
          asset: stakeConfig.stakingTokenSymbol,
        })}
      >
        <Value
          symbol={stakeConfig.stakingTokenSymbol}
          value={amount.toString()}
          tokenIcon={true}
          tooltipId={stakeConfig.stakingTokenSymbol}
        />
      </Row>
    </StakeTxConfirmationView>
  );
}
